import React, { useCallback, useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Card from "@/components/wpl/card/src";
import { CTAProps } from "@/components/wpl/content-block/src";
import { Image } from "@/content/templates/template.types";
import { expectContent } from "@/content/utils/expectContent";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    Article,
    FeaturedContentContainer,
    FeaturedVideo,
    NextBestAction,
    ProductFeature,
    Tutorial
} from "../../types";

export type NavigationCardProps = {
    eyebrowText: string | null;
    headerText?: string | null;
    bodyText?: string | null;
    ctaText?: string | null;
    ctaHref?: string | null;
    image?: Image | null;
    showLink: boolean;
};

export const NavigationCard = (props: NavigationCardProps) => {
    const {
        eyebrowText,
        headerText,
        bodyText,
        ctaText,
        ctaHref,
        image,
        showLink
    } = props;

    const pictureProps = image
        ? {
              image: {
                  src: image.url,
                  alt: image.description ? image.name : ""
              }
          }
        : undefined;

    const ctaProps: CTAProps | undefined =
        ctaText && ctaHref
            ? {
                  primaryLinkProps: {
                      text: ctaText,
                      href: ctaHref, // Localized from CPAPI?
                      type: "primary",
                      dataEventProps: {
                          "data-event": "clicked",
                          "data-event-component": "linkButton",
                          "data-product-key": "jiraSoftware",
                          "data-event-container": "agileCardLink"
                      }
                  }
              }
            : undefined;

    const localizedCtaHref = useLocalizedHref(ctaHref ?? "");

    return (
        <div
            css={[
                css({
                    "position": "relative",
                    "height": "100%",
                    "borderRadius": "4px",
                    // FIXME: These nested selectors are hacks to make Card look like a NavigationCard
                    "&:hover": {
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        ".navigation-card": {
                            // eslint-disable-next-line local/ensure-wac-token-usage
                            boxShadow: "1px 2px 2px 2px rgba(0,0,0,.22)"
                        },
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "a": {
                            textShadow: "none",
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            svg: {
                                transform: `translateX(${token("space.075")})`
                            }
                        }
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".navigation-card": {
                        // eslint-disable-next-line local/ensure-wac-token-usage
                        boxShadow: "1px 2px 2px 2px rgba(0,0,0,.08)",
                        transition: "all .5s ease-in-out"
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".text-section": {
                        height: "100%",
                        wordWrap: "break-word"
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".contentBlock-body": {
                        flexGrow: 1,
                        fontSize: "1rem",
                        lineHeight: "1.5"
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".contentBlock-eyebrow:after": {
                        content: "",
                        display: "block",
                        borderBlockStart: "1px solid",
                        opacity: ".2",
                        width: "40px"
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".contentBlock-linkFlex": {
                        // eslint-disable-next-line @atlaskit/design-system/no-physical-properties
                        marginBottom: "0 !important"
                    }
                }),
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                !showLink && css({ ".link-wrapper": { display: "none" } })
            ]}
        >
            <Card
                className="navigation-card"
                headerText={headerText || ""}
                bodyText={bodyText}
                eyebrowText={eyebrowText || ""}
                orientation="vertical"
                pictureProps={pictureProps}
                ctaProps={ctaProps}
            />
            {ctaHref && (
                <a
                    href={localizedCtaHref}
                    aria-label={headerText || ""}
                    css={css({
                        position: "absolute",
                        insetInlineStart: 0,
                        insetInlineEnd: 0,
                        insetBlockStart: 0,
                        insetBlockEnd: 0
                    })}
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-product-key="jiraSoftware"
                    data-event-container="agileCardLink"
                />
            )}
        </div>
    );
};

const isTutorial = (data: FeaturedContentContainer): data is Tutorial => {
    return (
        data.__typename === "ContentPlatformTutorial" ||
        data.__typename === "Tutorial"
    );
};

const isHubArticle = (data: FeaturedContentContainer): data is Article => {
    return (
        data.__typename === "ContentPlatformHubArticle" ||
        data.__typename === "MicrositeArticle"
    );
};

const isProductFeature = (
    data: FeaturedContentContainer
): data is ProductFeature => {
    return (
        data.__typename === "ContentPlatformProductFeature" ||
        data.__typename === "ProductFeature"
    );
};

const isFeaturedVideo = (
    data: FeaturedContentContainer
): data is FeaturedVideo => {
    return (
        data.__typename === "ContentPlatformFeaturedVideo" ||
        data.__typename === "FeaturedVideo"
    );
};

export const useCardProps = (
    data: FeaturedContentContainer[],
    showLink?: boolean
) => {
    const intl = useIntl();

    const tutorialEyeBrowText = intl.formatMessage({
        id: "agile.overview.navigation-cards.tutorial-eyebrow",
        defaultMessage: "Tutorial"
    });

    const tutorialCtaText = intl.formatMessage({
        id: "agile.overview.navigation-cards.tutorial-cta",
        defaultMessage: "Try this tutorial"
    });

    const articleEyeBrowText = intl.formatMessage({
        id: "agile.overview.navigation-cards.article-eyebrow",
        defaultMessage: "Article"
    });

    const articleCtaText = intl.formatMessage({
        id: "agile.overview.navigation-cards.article-cta",
        defaultMessage: "Read this article"
    });

    const productFeatureEyeBrowText = intl.formatMessage({
        id: "agile.overview.navigation-cards.product-feature-eyebrow",
        defaultMessage: "Product Feature"
    });

    const featuredVideoEyeBrowText = intl.formatMessage({
        id: "agile.overview.navigation-cards.featured-video-eyebrow",
        defaultMessage: "Video"
    });

    const buildCardProps = useCallback(
        // eslint-disable-next-line complexity
        (
            cards: NavigationCardProps[],
            data: NextBestAction | FeaturedContentContainer
        ) => {
            if (isTutorial(data)) {
                cards.push({
                    eyebrowText: tutorialEyeBrowText,
                    headerText: data.name,
                    bodyText: data.description,
                    ctaText: tutorialCtaText,
                    ctaHref: data.urlSlug,
                    image: data.tutorialBanner || undefined,
                    showLink: showLink ?? true
                });
            } else if (isHubArticle(data)) {
                cards.push({
                    eyebrowText: articleEyeBrowText,
                    headerText: data.articleName,
                    bodyText: data.description,
                    ctaText: articleCtaText,
                    ctaHref: data.urlSlug,
                    image: data?.articleBanner || undefined,
                    showLink: showLink ?? false
                });
            } else if (isProductFeature(data)) {
                cards.push({
                    eyebrowText: productFeatureEyeBrowText,
                    headerText: data.name,
                    bodyText: data.description,
                    ctaText: data.callToAction?.[0]?.text,
                    ctaHref: data.callToAction?.[0]?.url,
                    image: undefined,
                    showLink: showLink ?? true
                });
            } else if (isFeaturedVideo(data)) {
                cards.push({
                    eyebrowText: featuredVideoEyeBrowText,
                    headerText: data.videoName,
                    bodyText: data.description,
                    ctaText: data.callToActionText,
                    ctaHref: data.link,
                    image: undefined,
                    showLink: showLink ?? true
                });
            } else {
                console.error("Unknown next best action type", data);
            }

            return cards;
        },
        [
            tutorialEyeBrowText,
            tutorialCtaText,
            articleEyeBrowText,
            articleCtaText,
            productFeatureEyeBrowText,
            featuredVideoEyeBrowText,
            showLink
        ]
    );

    return useMemo(
        () => data.reduce(buildCardProps, []),
        [buildCardProps, data]
    );
};

type NavigationCardsProps = {
    cardsData: NextBestAction[];
};

export const NavigationCards = (props: NavigationCardsProps) => {
    const cardsProps = useCardProps(
        expectContent(props.cardsData, "Next best action not found"),
        true
    );

    const navigationCardCss = css({
        "paddingBlockEnd": "80px",
        "paddingBlockStart": "60px",
        // FIXME: These nested selectors are hacks to components we own
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".img-section": {
            backgroundSize: "cover",
            height: "70px",
            overflow: "hidden",
            borderRadius: "4px 4px 0 0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".contentBlock-eyebrow": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#42526E",
            fontSize: "12px",
            fontWeight: "400 !important",
            lineHeight: "24px",
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily:
                "Charlie Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif !important;"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h3": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#253858"
        }
    });

    return (
        <div css={navigationCardCss}>
            <Row layout="6-6" alignItems="center">
                {/* Ensure we're only ever rendering two cards max */}
                {cardsProps.slice(0, 2)?.map((props, index) => (
                    <React.Fragment key={index}>
                        <NavigationCard {...props} />
                    </React.Fragment>
                ))}
                {cardsProps.length === 1 && <div />}
            </Row>
        </div>
    );
};
